import React, { useState } from "react";
// UI Frameworks
import { TableCell } from "@mui/material";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteFilled, EditFilled } from "@ant-design/icons";
// Common Components
import Loading from "src/Components/Loading/Loading";
import Button from "src/Components/Button";
import StaticDropdown from "src/Components/Dropdown/StaticDropdown";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Hooks
import { useAppDispatch } from "src/App/hooks";
// Interfaces
import {
  IAdditional,
  IAdditionalHeaderProps,
} from "src/Tables/TableTemplate/tableTemplate";
import { IFundNoRiskTableReport } from "src/Interfaces/fundNoRisk";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Requests
import {
  deleteFundNoRisk,
  manualSell,
  manualBuy,
  bulkCancel,
  sellBulkDisable,
  sellBulkEnable,
  buyBulkDisable,
  buyBulkEnable,
} from "src/Requests/fundNoRiskTask";
// Reducers
import {
  setIsfundNoRiskAddEditOpen,
  setAddEditFundNotRiskFormValues,
} from "src/Features/FundNoRisk/fundNoRiskSlice";
import FundNoRiskBuyOrderTable from "./FundNoRiskBuyOrderTable";
import FundNoRiskTradeOrderTable from "./FundNoRiskTradeOrderTable";
import { Modal } from "src/Components";

enum ModalMode {
  Edit,
  SellOrder,
  BuyOrder,
  Closed,
}

enum Action {
  EnableSell,
  DisableSell,
  EnableBuy,
  DisableBuy,
  None,
}
export const FixedIncomeBulkActions = ({
  selectedRowsIds,
  onAction,
}: Pick<IAdditionalHeaderProps, "selectedRowsIds" | "onAction">) => {
  const [selectedAction, setSelectedAction] = useState<Action>(Action.None);
  const [loading, setLoading] = useState(false);

  const actionOptions: DropdownItem[] = [
    { id: Action.DisableSell, name: "خاموش کردن فروش" },
    { id: Action.EnableSell, name: "روشن کردن فروش" },
    { id: Action.EnableBuy, name: "روشن کردن خرید" },
    { id: Action.DisableBuy, name: "خاموش کردن خرید" },
  ];

  const getBulkActionFunction = () => {
    switch (selectedAction) {
      case Action.DisableSell:
        return sellBulkDisable;
      case Action.EnableSell:
        return sellBulkEnable;
      case Action.DisableBuy:
        return buyBulkDisable;
      case Action.EnableBuy:
        return buyBulkEnable;
      default:
        return null;
    }
  };

  const bulkActionsHandler = () => {
    if (selectedAction === Action.None) return;
    if (!selectedRowsIds || !selectedRowsIds.length) return;
    const actionFunction = getBulkActionFunction();
    if (!actionFunction) return;
    setLoading(true);
    actionFunction(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <div className="w-[250px] flex gap-2">
        <StaticDropdown
          dropdown={{
            name: "",
            placeholder: "عملیات گروهی",
          }}
          onChange={(_, v) => setSelectedAction(v)}
          options={actionOptions}
          value={selectedAction}
        />
        <Button
          text="اعمال"
          onClick={bulkActionsHandler}
          variant="outlined"
          disabled={
            selectedAction === Action.None ||
            !selectedRowsIds ||
            !selectedRowsIds.length
          }
          className="!w-[50px] table-btn m-10"
        />
      </div>
    </>
  );
};

const FixedIncomeAdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
}: IAdditionalHeaderProps) => {
  const bulkCancelOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    const request = bulkCancel(selectedRowsIds);

    request
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
        setSelectedRowsIds([]);
      })
      .catch(packetErrorHandler);
  };

  return (
    <>
      <TableCell />
      <TableCell>
        <div
          className={`transition-all rounded p-1 ${
            selectedRowsIds.length
              ? "hover:bg-red-100 cursor-pointer"
              : "cursor-not-allowed"
          }`}
          onClick={selectedRowsIds.length ? bulkCancelOrderHandler : undefined}
          aria-disabled={!selectedRowsIds.length}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
    </>
  );
};

const AdditionalCol: React.FC<{
  row: IFundNoRiskTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [modalMode, setModalMode] = useState(ModalMode.Closed);
  // local store
  const [loading, setLoading] = React.useState(false);
  // hooks
  const dispatch = useAppDispatch();

  const deleteHandler = () => {
    setLoading(true);
    deleteFundNoRisk(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const editHandler = () => {
    const firstSellPriorityInstrumentId =
      row.firstSellPriorityInstrumentId ?? row.FirstSellPriorityInstrumentId;
    const secondSellPriorityInstrumentId =
      row.secondSellPriorityInstrumentId ?? row.SecondSellPriorityInstrumentId;
    const buyInstrumentTargetInstrumentId =
      row.buyInstrumentTargetInstrumentId ??
      row.BuyInstrumentTargetInstrumentId;

    dispatch(setIsfundNoRiskAddEditOpen(true));
    dispatch(
      setAddEditFundNotRiskFormValues({
        id: row.id,
        accountId: row.userId,
        buyInstrumentTarget: buyInstrumentTargetInstrumentId ?? 0,
        firstSellPriority: firstSellPriorityInstrumentId ?? 0,
        secondSellPriority: secondSellPriorityInstrumentId ?? 0,
        minBuyPower: row.minBuyPower,
        buyPowerTarget: row.buyPowerTarget,
        buyingPowerCriterion: row.buyingPowerCriterion ?? 0,
      })
    );
  };

  const handelManualBuy = () => {
    manualBuy(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const handelManualSell = () => {
    manualSell(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.BuyOrder:
        return <FundNoRiskBuyOrderTable id={row.id} />;

      case ModalMode.SellOrder:
        return <FundNoRiskTradeOrderTable id={row.id} />;
    }
  };

  return (
    <>
      <TableCell>
        {modalMode !== ModalMode.Closed ? (
          <Modal
            title={modalMode === ModalMode.BuyOrder ? "معاملات خرید" : "معاملات فروش"}
            UI={getModalUI()}
            onClose={() => setModalMode(ModalMode.Closed)}
          />
        ) : (
          <></>
        )}
        <div className="w-16 mx-auto" onClick={editHandler}>
          <EditFilled />
        </div>
      </TableCell>
      <TableCell>
        <div className="w-16" onClick={deleteHandler}>
          {loading ? (
            <Loading size={16} className="!text-white" />
          ) : (
            <DeleteFilled />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Button
          text="معاملات فروش"
          onClick={() => {
            setModalMode(ModalMode.SellOrder);
          }}
          variant="contained"
        />
      </TableCell>
      <TableCell>
        <Button
          text="معاملات خرید"
          onClick={() => {
            setModalMode(ModalMode.BuyOrder);
          }}
          variant="contained"
        />
      </TableCell>
      <TableCell>
        <Button
          text="ارسال فروش"
          onClick={handelManualSell}
          variant="outlined"
          className="!text-red-700 !border-red-700 hover:!bg-red-700 hover:!text-white"
        />
      </TableCell>
      <TableCell>
        <Button
          text="ارسال خرید"
          onClick={handelManualBuy}
          variant="outlined"
          className="!text-green-700 !border-green-700 hover:!bg-green-700 hover:!text-white"
        />
      </TableCell>
    </>
  );
};

export const Additional: IAdditional<IFundNoRiskTableReport> = {
  getHeader: FixedIncomeAdditionalHeader,
  getRow: AdditionalCol,
};
